import { filterUserReactions } from './ReactionService';

export function getActivityScore(user, allUsers, totalOfPolls) {
  if (user.isModerator) return 0;

  const allUsersArr = Object.values(allUsers || {}).filter((currUser) => !currUser.isModerator);
  let userPoints = 0;

  // Calculate points of Talking
  const usersTalkTime = allUsersArr.map((currUser) => currUser.talk.totalTime);
  const maxTalkTime = Math.max(...usersTalkTime);
  if (maxTalkTime > 0) {
    userPoints += (user.talk.totalTime / maxTalkTime) * 2;
  }

  // Calculate points of Chatting
  const usersTotalOfMessages = allUsersArr.map((currUser) => currUser.totalOfMessages);
  const maxMessages = Math.max(...usersTotalOfMessages);
  if (maxMessages > 0) {
    userPoints += (user.totalOfMessages / maxMessages) * 2;
  }

  // Calculate points of Raise hand
  const usersRaiseHand = allUsersArr.map((currUser) => currUser?.raiseHand?.length || 0);
  const maxRaiseHand = Math.max(...usersRaiseHand);
  const userRaiseHand = user?.raiseHand?.length || 0;
  if (maxRaiseHand > 0) {
    userPoints += (userRaiseHand / maxRaiseHand) * 2;
  }

  // Calculate points of Reactions
  const usersReactions = allUsersArr.map((currUser) => currUser?.reactions?.length || 0);
  const maxReactions = Math.max(...usersReactions);
  const userReactions = user?.reactions?.length || 0;
  if (maxReactions > 0) {
    userPoints += (userReactions / maxReactions) * 2;
  }

  // Calculate points of Polls
  if (totalOfPolls > 0) {
    userPoints += (Object.values(user.answers || {}).length / totalOfPolls) * 2;
  }

  return userPoints;
}

export function getSumOfTime(eventsArr) {
  return eventsArr.reduce((prevVal, elem) => {
    if (elem?.sessions) {
      return prevVal + elem.sessions.reduce((prevVal2, session) => {
        if (session.leftOn > 0) {
          return prevVal2 + (session.leftOn - session.registeredOn);
        }
        return prevVal2 + (new Date().getTime() - session.registeredOn);
      }, 0);
    }
    if ((elem.stoppedOn || elem.leftOn) > 0) {
      return prevVal + ((elem.stoppedOn || elem.leftOn) - (elem.startedOn || elem.registeredOn));
    }
    return prevVal + (new Date().getTime() - (elem.startedOn || elem.registeredOn));
  }, 0);
}

export function getJoinTime(eventsArr) {
  return eventsArr.reduce((prevVal, elem) => {
    if (prevVal === 0 || elem.sessions[0].registeredOn < prevVal) {
      return elem.sessions[0].registeredOn;
    }
    return prevVal;
  }, 0);
}

export function getLeaveTime(eventsArr) {
  return eventsArr.reduce((prevVal, elem) => {
    if (elem.sessions[elem.sessions.length - 1].leftOn > prevVal) {
      return elem.sessions[elem.sessions.length - 1].leftOn;
    }
    return prevVal;
  }, 0);
}

export function tsToHHmmss(ts) {
  return (new Date(ts).toISOString().substr(11, 8));
}

const tableHeaderFields = [
  {
    id: 'name',
    defaultMessage: 'Name',
  },
  {
    id: 'moderator',
    defaultMessage: 'Moderator',
  },
  {
    id: 'activityScore',
    defaultMessage: 'Activity Score',
  },
  {
    id: 'colTalk',
    defaultMessage: 'Talk Time',
  },
  {
    id: 'colWebcam',
    defaultMessage: 'Webcam Time',
  },
  {
    id: 'colMessages',
    defaultMessage: 'Messages',
  },
  {
    id: 'colReactions',
    defaultMessage: 'Reactions',
  },
  {
    id: 'pollVotes',
    defaultMessage: 'Poll Votes',
  },
  {
    id: 'colRaiseHands',
    defaultMessage: 'Raise Hands',
  },
  {
    id: 'join',
    defaultMessage: 'Join',
  },
  {
    id: 'left',
    defaultMessage: 'Left',
  },
  {
    id: 'duration',
    defaultMessage: 'Duration',
  },
];

function buildUserRow(user, userValues, pollValues, intl) {
  const webcam = getSumOfTime(user.webcams);
  const duration = getSumOfTime(Object.values(user.intIds));
  const joinTime = getJoinTime(Object.values(user.intIds));
  const leaveTime = getLeaveTime(Object.values(user.intIds));

  const dateOpts = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  };

  const row = [
    user.name,
    user.isModerator.toString().toUpperCase(),
    intl.formatNumber(
      getActivityScore(user, userValues, pollValues.length),
      { minimumFractionDigits: 0, maximumFractionDigits: 1 },
    ),
    user.talk.totalTime > 0 ? tsToHHmmss(user.talk.totalTime) : '-',
    webcam > 0 ? tsToHHmmss(webcam) : '-',
    user.totalOfMessages,
    filterUserReactions(user).length,
    Object.keys(user.answers).length,
    user.raiseHand.length,
    intl.formatDate(joinTime, dateOpts),
    leaveTime > 0 ? intl.formatDate(leaveTime, dateOpts) : '-',
    tsToHHmmss(duration),
  ];

  for (let j = 0; j < pollValues.length; j += 1) {
    row.push(user.answers[pollValues[j].pollId] || '-');
  }

  return row;
}

// Builds the session-data table as an array of rows (row 0 = headers, last row = anonymous poll
// answers). Shared source of truth for both the CSV and the Excel (.xlsx) exports.
export function makeUserAOA(users, polls, intl) {
  const userValues = Object.values(users || {});
  const pollValues = Object.values(polls || {});

  const headers = tableHeaderFields.map(({ id, defaultMessage }) => intl.formatMessage({
    id: `app.learningDashboard.usersTable.${id}`,
    defaultMessage,
  }));
  pollValues.forEach((poll, i) => {
    // Poll question headers (remove spaces and line breaks)
    headers.push(poll.question.replace(/\s+/g, ' ').trim() || `Poll ${i + 1}`);
  });

  const rows = userValues.map((user) => buildUserRow(user, userValues, pollValues, intl));

  // Anonymous poll answers: name + empty base columns + one cell per poll.
  const anonymousRow = [
    intl.formatMessage({
      id: 'app.learningDashboard.pollsTable.anonymousRowName',
      defaultMessage: 'Anonymous',
    }),
    ...Array(tableHeaderFields.length - 1).fill(''),
    ...pollValues.map((poll) => poll.anonymousAnswers.join('\r\n')),
  ];
  rows.push(anonymousRow);

  return [headers, ...rows];
}

export function makeUserCSVData(users, polls, intl) {
  // Quote every field and escape embedded quotes so commas/newlines inside Persian text or poll
  // questions never break the column layout.
  return makeUserAOA(users, polls, intl)
    .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
}
